// src/pages/DebtStudio/components/Issues/fileManager.js
import { useMemo, useCallback, useEffect, useState } from "react";
import useFileManager from "components/FileManager/hooks/useFileManager";
import { useProfile } from "context/ProfileContext";
import { supabase } from "services/supabaseClient";
import {
  getDebtStudioPermissions,
  normalizeSharing,
} from "lib/permissions";

/* =============================================================================
   Issues File Manager (Adapter + Modal View)
   -----------------------------------------------------------------------------
   This module owns BOTH:
     1) Issues-specific file manager adapter (data access + business rules)
     2) Issues-specific FileManagerModal view config (columns/search/sort/labels)

   Keep this file as the single source of truth for "Open Issue" behaviors.
============================================================================= */

/* -----------------------------------------------------------------------------
 * Adapter hook: useIssuesFileManager
 * -------------------------------------------------------------------------- */

async function fetchOrgLibraryDeals({ orgId, pageSize = 500 }) {
  if (!orgId) return [];

  // Pull ALL ds_deals accessible to the selected org via ds_deal_org_access.
  // NOTE:
  // - We intentionally do NOT rely on org_id filters inside ds_deals, because
  //   library deals have org_id = null by design.
  // - We page through ds_deal_org_access so the library list is not silently
  //   truncated when an org has many published library deals.
  const out = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const to = from + pageSize - 1;

    const { data, error } = await supabase
      .from("ds_deal_org_access")
      .select(
        `
        deal_id,
        ds_deals:deal_id (
          *,
          issuers:issuer_id (
            legal_name
          )
        )
      `
      )
      .eq("org_id", orgId)
      .order("deal_id", { ascending: true })
      .range(from, to);

    if (error) throw error;

    const batch = Array.isArray(data) ? data : [];

    for (const row of batch) {
      if (row?.ds_deals) out.push(row.ds_deals);
    }

    hasMore = batch.length === pageSize;
    from += pageSize;
  }

  return out;
}

function isLibraryDealRow(r) {
  return String(r?.deal_class ?? "").toLowerCase() === "library";
}

export function useIssuesFileManager({ limit = 100 } = {}) {
  const { profile, selectedOrg } = useProfile();
  const profileId = profile?.id || null;

  const {
    records: rawWorkspaceRecords,
    recents,
    loading: workspaceLoading,
    error: workspaceError,
    fetchRecords: fetchWorkspaceRecords,
    addToRecents,
  } = useFileManager({
    table: "ds_deals",
    org: selectedOrg,
    limit,
    select: `
      *,
      issuers:issuer_id (
        legal_name
      )
    `,
  });

  // Library deals accessible to the org via ds_deal_org_access
  const [rawLibraryRecords, setRawLibraryRecords] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState(null);

  const fetchLibraryRecords = useCallback(async () => {
    const orgId = selectedOrg?.id;
    if (!orgId) {
      setRawLibraryRecords([]);
      setLibraryError(null);
      return;
    }

    setLibraryLoading(true);
    setLibraryError(null);

    try {
      const rows = await fetchOrgLibraryDeals({ orgId });
      setRawLibraryRecords(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setRawLibraryRecords([]);
      setLibraryError(e);
    } finally {
      setLibraryLoading(false);
    }
  }, [selectedOrg?.id]);

  // Refresh library deals when org changes (and on mount)
  useEffect(() => {
    fetchLibraryRecords();
  }, [fetchLibraryRecords]);

  // Combined refresh (workspace + library)
  const refresh = useCallback(async () => {
    await Promise.all([fetchWorkspaceRecords(), fetchLibraryRecords()]);
  }, [fetchWorkspaceRecords, fetchLibraryRecords]);

  // Merge workspace + library (dedupe by id)
  const mergedRawRecords = useMemo(() => {
    const out = [];
    const seen = new Set();

    const a = Array.isArray(rawWorkspaceRecords) ? rawWorkspaceRecords : [];
    const b = Array.isArray(rawLibraryRecords) ? rawLibraryRecords : [];

    for (const r of a) {
      if (!r?.id) continue;
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }

    for (const r of b) {
      if (!r?.id) continue;
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }

    return out;
  }, [rawWorkspaceRecords, rawLibraryRecords]);

  // Annotate with issuer_name + ownership/lock semantics
  const records = useMemo(() => {
    if (!Array.isArray(mergedRawRecords)) return [];

    return mergedRawRecords
      .map((r) => {
        // Policy alignment:
        // - library deals are locked for everyone
        // - saved non-library deals use centralized sharing permissions
        const isLibrary = isLibraryDealRow(r);
        const permissions = getDebtStudioPermissions(r, { id: profileId });
        if (
          !isLibrary &&
          !permissions.isOwner &&
          normalizeSharing(r?.sharing) === "user"
        ) {
          return null;
        }

        const isLocked = isLibrary ? true : permissions.isLocked;

        return {
          ...r,
          issuer_name: r?.issuers?.legal_name || null,
          ...permissions,
          isLocked,
          isLibrary,
        };
      })
      .filter(Boolean);
  }, [mergedRawRecords, profileId]);

  const openRecord = useCallback(
    (deal) => {
      addToRecents(deal);
    },
    [addToRecents]
  );

  // Owner-only delete (RLS enforced), blocked if referenced by any portfolio
  const deleteDeal = useCallback(
    async (dealId) => {
      if (!dealId) throw new Error("Invalid delete request.");
      if (!profileId) throw new Error("User not authenticated.");

      const deal = records.find((r) => r.id === dealId);
      if (!deal) throw new Error("Deal not found.");

      if (deal.isLibrary && !profile?.is_admin) {
        throw new Error("Only a platform admin can delete a library issue.");
      }

      if (!deal.isLibrary && !deal.canDelete) {
        throw new Error("You do not own this issue.");
      }

      const { data: refs, error: refErr } = await supabase
        .from("ds_portfolio_issues")
        .select("id")
        .eq("ds_deal_id", dealId)
        .limit(1);

      if (refErr) throw refErr;

      if (Array.isArray(refs) && refs.length > 0) {
        throw new Error(
          "This issue is included in one or more portfolios and cannot be deleted until it is removed from those portfolios."
        );
      }

      // Imported library issues are backed by ds_deals_lib. Delete the source
      // row so the entitlement sync cannot recreate the published issue.
      const deleteQuery = deal.isLibrary && deal.source_lib_id
        ? supabase.from("ds_deals_lib").delete().eq("id", deal.source_lib_id)
        : supabase.from("ds_deals").delete().eq("id", dealId);

      const { error: delErr } = await deleteQuery;

      if (delErr) throw delErr;

      await refresh();
    },
    [records, profileId, profile?.is_admin, refresh]
  );

  const loading = workspaceLoading || libraryLoading;

  // Prefer surfacing workspaceError if present, else libraryError
  const error = useMemo(() => {
    return workspaceError || libraryError || null;
  }, [workspaceError, libraryError]);

  const hasAccessError = useMemo(() => {
    if (!selectedOrg) return false;
    if (loading || error) return false;
    return Array.isArray(records) && records.length === 0;
  }, [selectedOrg, loading, error, records]);

  const isLockedRecord = useCallback(
    (r) => {
      if (!r) return false;
      if (typeof r.isLocked === "boolean") return r.isLocked;

      const isLibrary = isLibraryDealRow(r);
      if (isLibrary) return true;

      return getDebtStudioPermissions(r, { id: profileId }).isLocked;
    },
    [profileId]
  );

  return {
    selectedOrg,

    records,
    recents,
    loading,
    error,

    refresh,
    deleteRecord: deleteDeal,
    openRecord,

    isLockedRecord,

    hasAccessError,

    // NEW: FileRibbon label for the "+ New" action
    newLabel: "New Issue",
  };
}

/* -----------------------------------------------------------------------------
 * View config: issuesOpenView
 * -------------------------------------------------------------------------- */

const MONEY0 = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function normStr(x) {
  return String(x ?? "").trim();
}

function titleCase(x) {
  const s = normStr(x).toLowerCase();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function statusOrderVal(x) {
  const s = normStr(x).toLowerCase();
  const map = {
    draft: 10,
    prelim: 20,
    preliminary: 20,
    final: 30,
  };
  return map[s] ?? 999;
}

function getIssuerName(r) {
  return (
    r?.issuer_name ||
    r?.issuers?.legal_name ||
    r?.issuerNameRaw ||
    r?.issuer_name_raw ||
    ""
  );
}

function getIssueName(r) {
  return r?.deal_name || r?.issue_name || r?.name || "(Untitled)";
}

function getParAmount(r) {
  const v =
    r?.par_amount ??
    r?.parAmount ??
    r?.par ??
    r?.par_amount_total ??
    r?.parAmountTotal;

  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getStatus(r) {
  return r?.status || r?.deal_status || "";
}

function getLienPriority(r) {
  return r?.lien_priority ?? r?.lienPriority ?? "";
}

/* -----------------------------
   NEW: Dated Date helpers
------------------------------ */

function getDatedDate(r) {
  const v =
    r?.dated_date ??
    r?.datedDate ??
    r?.dated ??
    r?.issue_date ??
    r?.issueDate ??
    r?.dated_date_iso ??
    null;

  if (!v) return null;

  if (v instanceof Date && Number.isFinite(v.getTime())) return v;

  if (typeof v === "number") {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  return null;
}

function fmtISODate(d) {
  if (!d) return "";
  try {
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export const issuesOpenView = {
  title: "Open Saved Issue",

  // Keep default sort as Issuer A→Z
  defaultSort: { key: "issuer", asc: true },

  // folder-style grouping by Issuer (collapsed by default)
  grouping: {
    key: "issuer",
    header: "Issuer",
    defaultCollapsed: true,
    showToggleAll: true,
    value: (r) => normStr(getIssuerName(r)),
    label: (r) => {
      const s = normStr(getIssuerName(r));
      return s || "—";
    },
  },

  search: {
    placeholder: "Search issues…",
    predicate: (r, q) => {
      const issuer = normStr(getIssuerName(r)).toLowerCase();
      const name = normStr(getIssueName(r)).toLowerCase();
      const status = normStr(getStatus(r)).toLowerCase();
      const lien = normStr(getLienPriority(r)).toLowerCase();

      const par = getParAmount(r);
      const parStr = par ? String(Math.round(par)) : "";

      const dd = fmtISODate(getDatedDate(r)).toLowerCase();

      return (
        issuer.includes(q) ||
        name.includes(q) ||
        status.includes(q) ||
        lien.includes(q) ||
        parStr.includes(q) ||
        dd.includes(q)
      );
    },
  },

  selectedLabel: (r) => {
    const issuer = normStr(getIssuerName(r));
    const name = normStr(getIssueName(r));
    if (issuer && name) return `${issuer}, ${name}`;
    return name || issuer || "";
  },

  columns: [
    {
      key: "issuer",
      header: "Issuer",
      align: "left",
      sortValue: (r) => {
        const issuer = normStr(getIssuerName(r)).toLowerCase();
        const d = getDatedDate(r);

        // Newest dated date first within each issuer.
        // Missing dates sort last.
        const t = d ? d.getTime() : 0;
        const inverted = String(9999999999999 - t).padStart(13, "0");

        return `${issuer}|${inverted}|${normStr(getIssueName(r)).toLowerCase()}`;
      },
      render: (r) => {
        const issuer = getIssuerName(r);
        return issuer ? (
          <div className="truncate" title={issuer}>
            {issuer}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      key: "issue_name",
      header: "Issue Name",
      align: "left",
      sortValue: (r) => normStr(getIssueName(r)).toLowerCase(),
      showReadOnlyBadge: true,
      showLibraryBadge: true,
      render: (r) => {
        const name = getIssueName(r);
        return (
          <div className="truncate" title={name}>
            {name}
          </div>
        );
      },
    },
    {
      key: "par_amount",
      header: "Par Amount",
      align: "right",
      defaultAsc: false,
      sortValue: (r) => getParAmount(r),
      cellClassName: "tabular-nums",
      render: (r) => {
        const par = getParAmount(r);
        return par ? MONEY0.format(par) : <span className="text-muted">—</span>;
      },
    },
    {
      key: "dated_date",
      header: "Dated Date",
      align: "left",
      defaultAsc: false,
      sortValue: (r) => {
        const d = getDatedDate(r);
        return d ? d.getTime() : null;
      },
      render: (r) => {
        const d = getDatedDate(r);
        const s = fmtISODate(d);
        return s ? <span>{s}</span> : <span className="text-muted">—</span>;
      },
    },
    {
      key: "status",
      header: "Status",
      align: "left",
      defaultAsc: false,
      sortValue: (r) => statusOrderVal(getStatus(r)),
      render: (r) => {
        const s = getStatus(r);
        return s ? (
          <span>{titleCase(s)}</span>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
  ],
};
